import { change, stopSubmit, touch } from "redux-form";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    PASSWORD_RESET_PHONE_REQUEST_TOKEN_TRIGGER,
    PASSWORD_RESET_PHONE_VALIDATE_TOKEN_TRIGGER,
    PasswordResetPhoneRequestTokenNormalizedResponse,
    PasswordResetPhoneRequestTokenTriggerAction,
    PasswordResetPhoneValidateTokenNormalizedResponse,
    PasswordResetPhoneValidateTokenTriggerAction,
    passwordResetPhoneRequestTokenApi,
    passwordResetPhoneValidateTokenApi,
} from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhone.actions";
import { PASSWORD_RESET_PHONE_FORM_NAME } from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhoneForm";
import { usersPasswordResetSetNewUrl } from "@/mk2/apps/users/urls";
import { hasValidationViaSms } from "@/mk2/apps/users/utils";
import { FormPageType } from "@/mk2/constants/enums";
import { XHRAction, XHRFormError, normalizeError } from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { mkReduxFormOpenPage } from "@/mk2/helpers/form.actions";
import { showSuccessToast } from "@/mk2/helpers/toasts";
import { url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import {
    PASSWORD_RESET_WE_SENT_YOU_NEW_TOKEN_CALL,
    PASSWORD_RESET_WE_SENT_YOU_NEW_TOKEN_SMS,
    PHONE_NUMBER_SAVED,
} from "@/mk/autogenerated/translation";
import {
    phone_api_verification_send_token_url,
    phone_api_verification_validate_token_url,
} from "@/mk/urls/functions";

const logger = getLogger("PasswordResetPhoneV2.sagas");

interface ApiResponseRequestToken {
    body: PasswordResetPhoneRequestTokenNormalizedResponse;
}

function* requestToken({
    countryPrefix,
    rawPhoneNumber,
    resend,
    xhr,
}: PasswordResetPhoneRequestTokenTriggerAction & XHRAction) {
    yield put(passwordResetPhoneRequestTokenApi.request(countryPrefix, rawPhoneNumber));
    yield put(touch(PASSWORD_RESET_PHONE_FORM_NAME, "rawPhoneNumber", "countryPrefix"));

    try {
        const response: ApiResponseRequestToken = yield call(() =>
            xhr.post(phone_api_verification_send_token_url(), {
                mode: "password",
                requestTokenFd: {
                    countryPrefix,
                    rawPhoneNumber,
                },
            }),
        );

        // Stop submit
        yield put(
            change(
                PASSWORD_RESET_PHONE_FORM_NAME,
                "fullPhoneNumber",
                response.body.result.fullPhoneNumber,
            ),
        );
        yield put(stopSubmit(PASSWORD_RESET_PHONE_FORM_NAME));

        yield put(
            passwordResetPhoneRequestTokenApi.success(countryPrefix, rawPhoneNumber, response.body),
        );

        // Go to next step
        if (resend) {
            yield showSuccessToast(
                hasValidationViaSms(response.body.result.fullPhoneNumber)
                    ? PASSWORD_RESET_WE_SENT_YOU_NEW_TOKEN_SMS
                    : PASSWORD_RESET_WE_SENT_YOU_NEW_TOKEN_CALL,
            );
        } else {
            yield put(
                mkReduxFormOpenPage(
                    PASSWORD_RESET_PHONE_FORM_NAME,
                    FormPageType.PhoneVerificationTokenValidation,
                ),
            );
        }
    } catch (error) {
        if (resend && error.status === 400) {
            // chceme zobrazit chybu na formfielde rawPhoneNumber
            yield put(mkReduxFormOpenPage(PASSWORD_RESET_PHONE_FORM_NAME, FormPageType.Main));
        }
        yield handleXHRFormErrorSaga(PASSWORD_RESET_PHONE_FORM_NAME, error as XHRFormError, logger);
        yield put(
            passwordResetPhoneRequestTokenApi.failure(
                countryPrefix,
                rawPhoneNumber,
                normalizeError(error),
            ),
        );
    }
}

interface ApiResponseValidateToken {
    body: PasswordResetPhoneValidateTokenNormalizedResponse;
}

function* validateToken({
    token,
    fullPhoneNumber,
    xhr,
}: PasswordResetPhoneValidateTokenTriggerAction & XHRAction) {
    yield put(passwordResetPhoneValidateTokenApi.request(token, fullPhoneNumber));
    yield put(touch(PASSWORD_RESET_PHONE_FORM_NAME, "token"));

    try {
        const response: ApiResponseValidateToken = yield call(() =>
            xhr.post(phone_api_verification_validate_token_url(), {
                mode: "password",
                validateTokenFd: {
                    token,
                    fullPhoneNumber,
                },
            }),
        );

        const gotoUrl = url(usersPasswordResetSetNewUrl, {
            token: response.body.result.passwordResetToken,
        });
        yield handleXHRFormSuccessSaga(PASSWORD_RESET_PHONE_FORM_NAME, gotoUrl, PHONE_NUMBER_SAVED);
        yield put(passwordResetPhoneValidateTokenApi.success(token, fullPhoneNumber, null));
    } catch (error) {
        yield handleXHRFormErrorSaga(PASSWORD_RESET_PHONE_FORM_NAME, error as XHRFormError, logger);
        yield put(
            passwordResetPhoneValidateTokenApi.failure(
                token,
                fullPhoneNumber,
                normalizeError(error),
            ),
        );
    }
}

export default function* root() {
    yield all([
        takeLatest(PASSWORD_RESET_PHONE_REQUEST_TOKEN_TRIGGER, requestToken),
        takeLatest(PASSWORD_RESET_PHONE_VALIDATE_TOKEN_TRIGGER, validateToken),
    ]);
}
